import { safeGetItem, safeSetItem, type StorageArea } from "../utils/safeStorage";

export type AnalyticsDeviceInfo = {
  deviceType: "mobile" | "tablet" | "desktop";
  browser: string;
  os: string;
  isBot: boolean;
};

export type AnalyticsLocation = {
  country?: string;
  region?: string;
  city?: string;
};

export interface AnalyticsEventInput {
  eventName: string;
  eventCategory?: string;
  artist?: string;
  releaseKey?: string;
  releaseName?: string;
  trackId?: string;
  trackName?: string;
  platform?: string;
  metadata?: Record<string, unknown>;
  duration?: number;
  referrer?: string;
  immediate?: boolean;
  onSent?: (eventId: string | null) => void;
}

export interface AnalyticsCampaignContext {
  adMode: boolean;
  adModeSession: boolean;
  utm?: Record<string, string>;
  adId?: string;
}

export const ANALYTICS_BUS_EVENT = "truevined:analytics";
export const SESSION_EVENT_LIMIT = 400;
export const CLICK_DEDUPE_MS = 800;

const CAMPAIGN_KEY = "truevined_analytics_campaign";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "gclid", "ttclid"];
const VISITOR_KEY = "truevined_analytics_visitor_id";
const SESSION_KEY = "truevined_analytics_session_id";
const SESSION_STARTED_KEY = "truevined_analytics_session_started";
const EVENT_COUNT_KEY = "truevined_analytics_event_count";
const LOCATION_KEY = "truevined_analytics_location";
const LOCATION_TTL_MS = 24 * 60 * 60 * 1000;
const SESSION_IDLE_MS = 30 * 60 * 1000;

const BOT_PATTERNS = [
  "bot",
  "crawler",
  "spider",
  "scraper",
  "curl",
  "wget",
  "python",
  "java",
  "googlebot",
  "bingbot",
  "yandexbot",
  "baiduspider",
  "facebookexternalhit",
  "twitterbot",
  "slackbot",
  "telegrambot",
  "whatsapp",
  "linkedinbot",
  "pinterest",
  "headless",
  "phantom",
  "selenium",
  "puppeteer",
  "playwright",
  "lighthouse",
  "pagespeed",
];

const hasWindow = () => typeof window !== "undefined";

function safeGet(area: StorageArea, key: string): string | null {
  return safeGetItem(key, area);
}

function safeSet(area: StorageArea, key: string, value: string) {
  safeSetItem(key, value, area);
}

const localStore = (): StorageArea => "local";
const sessionStore = (): StorageArea => "session";

export function createAnalyticsId(prefix: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}_${random}`;
}

export function getVisitorId(): string {
  let visitorId = safeGet(localStore(), VISITOR_KEY);
  if (!visitorId) {
    visitorId = safeGet(sessionStore(), VISITOR_KEY);
  }
  if (!visitorId) {
    visitorId = createAnalyticsId("visitor");
    safeSet(localStore(), VISITOR_KEY, visitorId);
    safeSet(sessionStore(), VISITOR_KEY, visitorId);
  }
  return visitorId;
}

export function getSessionId(): string {
  const now = Date.now();
  const started = Number(safeGet(sessionStore(), SESSION_STARTED_KEY) || 0);
  let sessionId = safeGet(sessionStore(), SESSION_KEY);
  if (!sessionId || (started && now - started > SESSION_IDLE_MS)) {
    sessionId = createAnalyticsId("session");
    safeSet(sessionStore(), SESSION_KEY, sessionId);
    safeSet(sessionStore(), EVENT_COUNT_KEY, "0");
    safeSet(sessionStore(), CAMPAIGN_KEY, "");
  }
  safeSet(sessionStore(), SESSION_STARTED_KEY, String(now));
  return sessionId;
}

export function consumeEventBudget(): boolean {
  const count = Number(safeGet(sessionStore(), EVENT_COUNT_KEY) || 0);
  if (count >= SESSION_EVENT_LIMIT) return false;
  safeSet(sessionStore(), EVENT_COUNT_KEY, String(count + 1));
  return true;
}

export function beginTrackedEvent(options?: { consume?: boolean }): { sessionId: string; allowed: boolean } {
  const sessionId = getSessionId();
  if (options?.consume === false) return { sessionId, allowed: true };
  return { sessionId, allowed: consumeEventBudget() };
}

export function isBotOrCrawler(): boolean {
  if (!hasWindow()) return true;
  const userAgent = navigator.userAgent.toLowerCase();
  if ((navigator as Navigator & { webdriver?: boolean }).webdriver) return true;
  return BOT_PATTERNS.some((pattern) => userAgent.includes(pattern));
}

export function getDeviceInfo(): AnalyticsDeviceInfo {
  if (!hasWindow()) return { deviceType: "desktop", browser: "unknown", os: "unknown", isBot: true };
  const width = window.innerWidth;
  const deviceType = width < 768 ? "mobile" : width < 1024 ? "tablet" : "desktop";
  const userAgent = navigator.userAgent;
  let browser = "Other";
  if (/edg\//i.test(userAgent)) browser = "Edge";
  else if (/opr\//i.test(userAgent)) browser = "Opera";
  else if (/samsungbrowser/i.test(userAgent)) browser = "Samsung Internet";
  else if (/chrome|crios/i.test(userAgent)) browser = "Chrome";
  else if (/firefox|fxios/i.test(userAgent)) browser = "Firefox";
  else if (/safari/i.test(userAgent)) browser = "Safari";
  let os = "Other";
  if (/iphone|ipad|ipod/i.test(userAgent)) os = "iOS";
  else if (/android/i.test(userAgent)) os = "Android";
  else if (/windows/i.test(userAgent)) os = "Windows";
  else if (/mac os/i.test(userAgent)) os = "macOS";
  else if (/cros/i.test(userAgent)) os = "ChromeOS";
  else if (/linux/i.test(userAgent)) os = "Linux";
  return { deviceType, browser, os, isBot: isBotOrCrawler() };
}

let pendingLocation: Promise<AnalyticsLocation | undefined> | null = null;

export function getVisitorLocation(): Promise<AnalyticsLocation | undefined> {
  if (!hasWindow()) return Promise.resolve(undefined);
  const cached = safeGet(localStore(), LOCATION_KEY);
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as { expiresAt: number; value?: AnalyticsLocation };
      if (parsed.expiresAt > Date.now()) return Promise.resolve(parsed.value);
    } catch {
      /* fall through to refetch */
    }
  }
  if (pendingLocation) return pendingLocation;
  pendingLocation = fetch("/.netlify/functions/Geo", { cache: "no-store" })
    .then(async (response) => {
      if (!response.ok) return undefined;
      const data = (await response.json()) as { country?: string | null; region?: string | null; city?: string | null };
      const value: AnalyticsLocation | undefined = data.country
        ? { country: data.country, region: data.region || undefined, city: data.city || undefined }
        : undefined;
      safeSet(localStore(), LOCATION_KEY, JSON.stringify({ expiresAt: Date.now() + LOCATION_TTL_MS, value }));
      return value;
    })
    .catch(() => undefined)
    .finally(() => {
      pendingLocation = null;
    });
  return pendingLocation;
}

export function getCachedVisitorLocation(): AnalyticsLocation | undefined {
  if (!hasWindow()) return undefined;
  const cached = safeGet(localStore(), LOCATION_KEY);
  if (!cached) return undefined;
  try {
    const parsed = JSON.parse(cached) as { expiresAt: number; value?: AnalyticsLocation };
    return parsed.expiresAt > Date.now() ? parsed.value : undefined;
  } catch {
    return undefined;
  }
}

function readStoredCampaign(): AnalyticsCampaignContext | null {
  const stored = safeGet(sessionStore(), CAMPAIGN_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as AnalyticsCampaignContext & { sessionId?: string };
    if (parsed.sessionId !== safeGet(sessionStore(), SESSION_KEY)) return null;
    return { adMode: Boolean(parsed.adMode), adModeSession: Boolean(parsed.adModeSession), utm: parsed.utm, adId: parsed.adId };
  } catch {
    return null;
  }
}

export function getCampaignContext(search?: string): AnalyticsCampaignContext {
  if (!hasWindow()) return { adMode: false, adModeSession: false };
  const sessionId = getSessionId();
  const params = new URLSearchParams(search ?? window.location.search);
  const adMode = params.has("adMode") || params.has("ad");
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) utm[key] = value.slice(0, 120);
  }
  const stored = readStoredCampaign();
  const isNewCampaign = Object.keys(utm).length > 0 || params.has("tv_ad");
  const mergedUtm = isNewCampaign ? utm : stored?.utm ?? {};
  const incomingAdId = params.get("tv_ad");
  const adId = isNewCampaign ? (incomingAdId && /^[a-z0-9]{20,64}$/.test(incomingAdId) ? incomingAdId : undefined) : stored?.adId;
  const context: AnalyticsCampaignContext = {
    adMode,
    adModeSession: adMode || Boolean(stored?.adModeSession),
    utm: Object.keys(mergedUtm).length > 0 ? mergedUtm : undefined,
    adId,
  };
  if (adMode || isNewCampaign) {
    safeSet(sessionStore(), CAMPAIGN_KEY, JSON.stringify({ adMode: context.adModeSession, adModeSession: context.adModeSession, utm: context.utm, adId, sessionId }));
  }
  return context;
}

const lastClickAt = new Map<string, number>();

export function shouldRecordClick(target: string, now = Date.now()): boolean {
  const previous = lastClickAt.get(target);
  if (previous && now - previous < CLICK_DEDUPE_MS) return false;
  lastClickAt.set(target, now);
  if (lastClickAt.size > 200) {
    for (const [key, at] of lastClickAt) {
      if (now - at > CLICK_DEDUPE_MS * 10) lastClickAt.delete(key);
    }
  }
  return true;
}

export function emitAnalyticsEvent(input: AnalyticsEventInput) {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent<AnalyticsEventInput>(ANALYTICS_BUS_EVENT, { detail: input }));
}

export function emitAnalyticsEventAndWait(input: AnalyticsEventInput, timeoutMs = 2500): Promise<string | null> {
  if (!hasWindow()) return Promise.resolve(null);
  return new Promise<string | null>((resolve) => {
    let settled = false;
    const finish = (eventId: string | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(eventId);
    };
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    window.setTimeout(() => emitAnalyticsEvent({ ...input, immediate: true, onSent: finish }), 0);
  });
}

export function getReferrer(): string | undefined {
  if (!hasWindow()) return undefined;
  return document.referrer || undefined;
}
